import { useCallback, useMemo, useState } from 'react';
import { searchCompanyInvoiceService as defaultService } from '../services/searchCompanyInvoiceService';
import { useGetSearchCompanyInvoice } from './useGetSearchCompanyInvoice';

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 15;

// ─── Pure Logic ───────────────────────────────────────────────────────────────

export const buildSearchParams = ({ userId, from, size, sort, filter }) => ({
  userId,
  from,
  size,
  sort,
  filter,
});

export const deriveInvoicePageState = invoicePage => ({
  invoices: invoicePage?.invoices ?? [],
  totalSize: invoicePage?.totalSize ?? 0,
  totalPages: invoicePage?.totalPages ?? 0,
});

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Orchestrating hook for the invoice table.
 *
 * Manages pending filter state (edited by the user) vs committed filter state
 * (what the query actually runs against). Filters are committed — and page
 * reset to 0 — only when `apply()` is called. Date range changes coming in
 * from outside (fromDate / toDate props) auto-reset the page because the
 * stale page number is meaningless against a new date window; any in-flight
 * request for the old page is discarded by the requestId race guard inside
 * useGetSearchCompanyInvoice.
 *
 * `service` is injected for testability — pass a mock in tests.
 */
export function useSearchCompanyInvoice(
  userId,
  fromDate,
  toDate,
  initialFilters = {},
  service = defaultService,
) {
  // ─── Pending filter state (user is editing, not yet applied) ─────────────
  const [pendingSearch, setPendingSearch] = useState(initialFilters.search ?? '');
  const [pendingStatus, setPendingStatus] = useState(initialFilters.status ?? '');
  const [pendingPlaceId, setPendingPlaceId] = useState(initialFilters.placeId ?? '');

  // ─── Committed filter state (drives the active query) ────────────────────
  const [committedSearch, setCommittedSearch] = useState(initialFilters.search ?? '');
  const [committedStatus, setCommittedStatus] = useState(initialFilters.status ?? '');
  const [committedPlaceId, setCommittedPlaceId] = useState(initialFilters.placeId ?? '');
  const [committedFromDate, setCommittedFromDate] = useState(fromDate);
  const [committedToDate, setCommittedToDate] = useState(toDate);

  // ─── Pagination + sort ───────────────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState('CREATED_DESC');

  const params = useMemo(
    () =>
      buildSearchParams({
        userId,
        from: page * PAGE_SIZE,
        size: PAGE_SIZE,
        sort,
        filter: {
          ...(committedSearch ? { invoiceNumber: committedSearch } : {}),
          ...(committedStatus ? { status: committedStatus } : {}),
          ...(committedPlaceId ? { placeId: committedPlaceId } : {}),
          ...(committedFromDate ? { fromDate: committedFromDate } : {}),
          ...(committedToDate ? { toDate: committedToDate } : {}),
        },
      }),
    [userId, committedFromDate, committedToDate, page, sort, committedSearch, committedStatus, committedPlaceId],
  );

  const { status, invoicePage, error, refresh } = useGetSearchCompanyInvoice(
    params,
    service,
  );

  const derived = useMemo(() => deriveInvoicePageState(invoicePage), [
    invoicePage,
  ]);

  // ─── Actions ─────────────────────────────────────────────────────────────

  const apply = useCallback((overrideFromDate, overrideToDate) => {
    setCommittedSearch(pendingSearch);
    setCommittedStatus(pendingStatus);
    setCommittedPlaceId(pendingPlaceId);
    setCommittedFromDate(overrideFromDate ?? fromDate);
    setCommittedToDate(overrideToDate ?? toDate);
    setPage(0);
  }, [pendingSearch, pendingStatus, pendingPlaceId, fromDate, toDate]);

  const changePage = useCallback(nextPage => {
    setPage(nextPage);
  }, []);

  return {
    status,
    error,

    invoices: derived.invoices,
    totalSize: derived.totalSize,
    totalPages: derived.totalPages,
    page,
    pageSize: PAGE_SIZE,

    sort,
    search: pendingSearch,
    statusFilter: pendingStatus,
    placeIdFilter: pendingPlaceId,

    setSearch: setPendingSearch,
    setStatusFilter: setPendingStatus,
    setPlaceIdFilter: setPendingPlaceId,
    setSort,
    setPage: changePage,
    apply,
    refresh,
  };
}
